import type { ISpawnableObject } from '@interfaces/event';
import type { RealtimeEntityPropertyValue } from '@interfaces/realtimeEntities';

// Both known UE region types are scriptable (admin shows regions of either
// type, splitting them into Triggers/Areas tabs by this same value). The same
// strings are the runtime-spawnable typeIds whose TriggerName makes them a
// region, so one set serves the build metadata and the spawn schema alike.
export const SCRIPTABLE_REGION_TYPES = new Set([
  'MytaAITrigger',
  'MytaVideoTrigger',
]);

// The spawnable-schema property that carries a trigger region's identity: the
// engine reports it as audioTrigger.aiTriggerId and the build exports it as
// regions[].name. The schema itself does not mark it, so the name is fixed
// here the same way the region types above are.
export const TRIGGER_NAME_PROPERTY = 'TriggerName';

// `providerUtils.isAITrigger` reads this prefix as "an AI trigger" and defaults
// a script's playback policy to ALWAYS, the way placed AI regions behave. Only
// an AI trigger gets it: a video trigger seeded with it would inherit that
// playback default while a placed one does not.
export const AI_TRIGGER_NAME_SEED_PREFIX = 'AITrigger_';
export const TRIGGER_NAME_SEED_PREFIX = 'Trigger_';

/**
 * Reads a trigger name out of a property bag, trimmed; anything but a
 * non-empty string reads as no name.
 *
 * @param properties - Property bag of a spawned entity or its saved snapshot.
 * @returns The trimmed name, or an empty string.
 */
export const readTriggerName = (
  properties: Record<string, RealtimeEntityPropertyValue> | undefined,
): string => {
  const raw = properties?.[TRIGGER_NAME_PROPERTY];
  return typeof raw === 'string' ? raw.trim() : '';
};

/**
 * Tells whether a spawnable type declares a string TriggerName — the only
 * shape the hub can seed and the inspector can edit as a region name.
 *
 * @param schema - The type's entry in the build's spawnableObjects schema.
 * @returns True when the type carries a `TriggerName: string` property.
 */
export const declaresTriggerName = (
  schema: ISpawnableObject | undefined,
): boolean =>
  !!schema?.properties?.some(
    (property) =>
      property?.name === TRIGGER_NAME_PROPERTY && property.type === 'string',
  );

/**
 * Builds the name a freshly spawned trigger region is given. Unique across
 * sessions and admins because it is cut from the entity id, unlike a
 * per-session counter; an AI trigger is prefixed so scripts bound to it get
 * the AI-trigger playback default, any other region type gets a neutral one.
 *
 * @param entityId - Wire id the entity is spawned with.
 * @param typeId - The spawnable type, deciding the prefix.
 * @returns The seed: the type's prefix plus the first eight hex digits of the id.
 */
export const seedTriggerName = (entityId: string, typeId: string): string => {
  const prefix =
    typeId === 'MytaAITrigger'
      ? AI_TRIGGER_NAME_SEED_PREFIX
      : TRIGGER_NAME_SEED_PREFIX;
  return `${prefix}${entityId.replace(/-/g, '').slice(0, 8)}`;
};

/**
 * Builds the property bag a spawn payload should carry for a type. A region
 * type gets its TriggerName seeded so the first Save never persists an
 * unnamed region — an empty name reports an empty `aiTriggerId` shared by every
 * other unnamed region. Types without the property get nothing.
 *
 * @param schema - The type's entry in the build's spawnableObjects schema.
 * @param entityId - Wire id the entity is spawned with.
 * @returns The bag to send, or undefined when there is nothing to seed.
 */
export const seedSpawnProperties = (
  schema: ISpawnableObject | undefined,
  entityId: string,
): Record<string, RealtimeEntityPropertyValue> | undefined =>
  schema && declaresTriggerName(schema)
    ? { [TRIGGER_NAME_PROPERTY]: seedTriggerName(entityId, schema.typeId) }
    : undefined;
